/**
 * Chunked file upload utility
 * Splits large files into manageable pieces to bypass proxy limits
 * and prevent browser memory crashes.
 */

const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB per chunk

/**
 * Upload a file using chunked upload.
 * @param {File} file - The file to upload
 * @param {Object} options
 * @param {Function} options.getToken - Returns auth token
 * @param {Object} options.metadata - { family_id, event_id?, album_id?, caption?, upload_type?, story_id? }
 * @param {Function} options.onProgress - (percent) => void
 * @param {AbortSignal} options.signal - AbortController signal for cancellation
 * @returns {Promise<Object>} - Server response from complete endpoint
 */
export async function chunkedUpload(file, { getToken, metadata = {}, onProgress, signal } = {}) {
  // IMPORTANT: use relative URLs so production hits the production backend, not
  // whatever REACT_APP_BACKEND_URL was baked in at build time. Matches the
  // pattern used by the rest of the app (migrated March 2026).
  const API = '';
  const token = getToken ? getToken() : localStorage.getItem('session_token');

  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

  // Step 1: Initialize upload session
  const initForm = new FormData();
  initForm.append('filename', file.name);
  initForm.append('content_type', file.type);
  initForm.append('total_size', file.size.toString());
  initForm.append('total_chunks', totalChunks.toString());

  const initRes = await fetch(`${API}/api/media-upload/init`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
    body: initForm,
    signal,
  });

  if (!initRes.ok) {
    const err = await initRes.json().catch(() => ({ detail: 'Upload initialization failed' }));
    throw new Error(err.detail || 'Upload initialization failed');
  }

  const { upload_id } = await initRes.json();

  // Step 2: Send chunks sequentially
  try {
    for (let i = 0; i < totalChunks; i++) {
      if (signal?.aborted) {
        throw new DOMException('Upload cancelled', 'AbortError');
      }

      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);

      const chunkForm = new FormData();
      chunkForm.append('chunk_index', i.toString());
      chunkForm.append('file', chunk, `chunk_${i}`);

      const chunkRes = await fetch(`${API}/api/media-upload/chunk/${upload_id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: chunkForm,
        signal,
      });

      if (!chunkRes.ok) {
        const err = await chunkRes.json().catch(() => ({ detail: 'Chunk upload failed' }));
        throw new Error(err.detail || `Chunk ${i + 1} upload failed`);
      }

      if (onProgress) {
        onProgress(Math.round(((i + 1) / totalChunks) * 90)); // 0-90% for chunks
      }
    }

    // Step 3: Complete upload
    const completeForm = new FormData();
    completeForm.append('family_id', metadata.family_id || '');
    if (metadata.event_id) completeForm.append('event_id', metadata.event_id);
    if (metadata.album_id) completeForm.append('album_id', metadata.album_id);
    if (metadata.caption) completeForm.append('caption', metadata.caption);
    completeForm.append('upload_type', metadata.upload_type || 'gallery');
    if (metadata.story_id) completeForm.append('story_id', metadata.story_id);

    const completeRes = await fetch(`${API}/api/media-upload/complete/${upload_id}`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: completeForm,
      signal,
    });

    if (!completeRes.ok) {
      const err = await completeRes.json().catch(() => ({ detail: 'Upload finalization failed' }));
      throw new Error(err.detail || 'Upload finalization failed');
    }

    if (onProgress) onProgress(100);

    return await completeRes.json();
  } catch (error) {
    // Attempt to cancel on the server if something went wrong
    try {
      await fetch(`${API}/api/media-upload/cancel/${upload_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
    } catch (_) {
      // ignore cleanup errors
    }
    throw error;
  }
}

/**
 * Get video duration using HTML5 Video element.
 * @param {File} file
 * @returns {Promise<number>} - Duration in seconds
 */
export function getVideoDuration(file) {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';

    const url = URL.createObjectURL(file);
    video.src = url;

    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(video.duration);
    };

    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read video metadata'));
    };

    // Timeout after 10 seconds
    setTimeout(() => {
      URL.revokeObjectURL(url);
      resolve(0); // Can't determine duration, allow upload
    }, 10000);
  });
}

/**
 * Detect HEIC/HEIF files. Safari often reports an empty `file.type` for these,
 * so we fall back to checking the filename extension.
 */
export function isHeicFile(file) {
  const name = (file?.name || '').toLowerCase();
  const type = (file?.type || '').toLowerCase();
  return (
    type === 'image/heic' ||
    type === 'image/heif' ||
    name.endsWith('.heic') ||
    name.endsWith('.heif')
  );
}

/**
 * Convert a HEIC/HEIF file to a JPEG File in the browser.
 * Returns the original file unchanged if conversion fails.
 */
export async function convertHeicToJpeg(file, quality = 0.9) {
  if (!isHeicFile(file)) return file;
  try {
    const heic2any = (await import('heic2any')).default;
    const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality });
    const blob = Array.isArray(converted) ? converted[0] : converted;
    const newName = (file.name || 'photo').replace(/\.(heic|heif)$/i, '.jpg');
    return new File([blob], newName, { type: 'image/jpeg', lastModified: Date.now() });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('HEIC conversion failed, uploading original:', e);
    return file;
  }
}

/**
 * Compress / downscale a large raster photo in the browser before upload.
 * - Only touches raster images (skips video, animated GIF, and SVG).
 * - Leaves images at/under `thresholdMB` untouched so quality isn't lost.
 * - Never upscales; caps the long edge at `maxDimension`.
 * - Applies EXIF orientation so portrait photos aren't rotated.
 * Returns the original file if compression isn't beneficial or fails.
 * @param {File} file
 * @param {{thresholdMB?: number, maxDimension?: number, quality?: number}} opts
 * @returns {Promise<File>}
 */
export async function compressImage(file, { thresholdMB = 3, maxDimension = 2560, quality = 0.82 } = {}) {
  try {
    const type = (file?.type || '').toLowerCase();
    const isRaster = type.startsWith('image/') && type !== 'image/gif' && type !== 'image/svg+xml';
    if (!isRaster) return file;
    // Leave already-small images untouched to preserve full quality.
    if (file.size <= thresholdMB * 1024 * 1024) return file;

    // Decode with EXIF orientation applied (Safari/Chrome 15+); fall back if unsupported.
    let bitmap;
    try {
      bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch (_) {
      bitmap = await createImageBitmap(file);
    }

    const { width, height } = bitmap;
    const scale = Math.min(1, maxDimension / Math.max(width, height));
    const targetW = Math.max(1, Math.round(width * scale));
    const targetH = Math.max(1, Math.round(height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bitmap, 0, 0, targetW, targetH);
    if (bitmap.close) bitmap.close();

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
    // Only keep the compressed version if it's actually smaller.
    if (!blob || blob.size >= file.size) return file;

    const base = (file.name || 'photo').replace(/\.[^.]+$/, '');
    return new File([blob], `${base}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('Image compression failed, uploading original:', e);
    return file;
  }
}

/**
 * Validate a media file before upload.
 * @param {File} file
 * @returns {Promise<{valid: boolean, error?: string, duration?: number, sizeMB: number}>}
 */
export async function validateMediaFile(file) {
  const sizeMB = file.size / (1024 * 1024);
  const name = (file.name || '').toLowerCase();
  const isVideo = file.type.startsWith('video/');
  // Safari can leave file.type empty for HEIC/HEIF — fall back to extension.
  const isImage =
    file.type.startsWith('image/') ||
    isHeicFile(file) ||
    /\.(jpe?g|png|gif|webp|bmp|tiff?)$/i.test(name);

  if (!isVideo && !isImage) {
    return {
      valid: false,
      error: "We couldn't recognize this file type. Please upload a JPG, PNG, HEIC, or video.",
      sizeMB,
    };
  }

  // Size limits — images bumped to 25MB to accommodate modern phone photos.
  const maxSizeMB = isVideo ? 200 : 25;
  if (sizeMB > maxSizeMB) {
    return {
      valid: false,
      error: isVideo
        ? `Video is ${sizeMB.toFixed(1)}MB — exceeds the ${maxSizeMB}MB limit. Try recording in 1080p instead of 4K, or trim to a shorter clip.`
        : `Image is ${sizeMB.toFixed(1)}MB — exceeds the ${maxSizeMB}MB limit. Try exporting at a smaller size from your Photos app.`,
      sizeMB,
    };
  }

  // Video duration check
  if (isVideo) {
    try {
      const duration = await getVideoDuration(file);
      const maxDurationMinutes = 5;
      if (duration > maxDurationMinutes * 60) {
        return {
          valid: false,
          error: `Video is ${Math.floor(duration / 60)}m ${Math.floor(duration % 60)}s — maximum is ${maxDurationMinutes} minutes. Please trim your video.`,
          sizeMB,
          duration,
        };
      }
      return { valid: true, sizeMB, duration };
    } catch {
      // Can't check duration, allow the upload
      return { valid: true, sizeMB };
    }
  }

  return { valid: true, sizeMB };
}
